import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useConsultation } from "../../ConsultationContext";
import { getBiomarkerCategoryColor } from "../../../platform/store/helper";
import { BIOMARKER_CATEGORY_LABELS } from "../../../platform/components/score/usePanelData";
import {
  METRIC_TILE_SYSTEMS,
  MetricTileKey,
} from "../../data/consultationTypes";

// `METRIC_TILE_SYSTEMS` is a static UI lookup (tile id → list of
// consultation sub-step ids that should highlight when active). It's a
// frontend convention, not data the backend will ever ship — kept here as
// a direct import. Actual tile VALUES come from the consultation-prep
// payload via `data.prep.mainMetricsTiles`.
const tileMatchesActiveSystem = (
  tileKey: MetricTileKey,
  activeSubId: string
): boolean =>
  (METRIC_TILE_SYSTEMS[tileKey] ?? []).includes(activeSubId);

/**
 * Maps a 0-100 metric score into the biomarker scoreCategory scale (1-4).
 * Keying both the label and the chip colour off this single value guarantees
 * they always agree (1=Borderline yellow, 2=Normal blue, 3=Optimal green,
 * 4=Out of range red — same convention the score screen uses for biomarkers).
 */
const scoreToBiomarkerCategory = (score: number): 1 | 2 | 3 | 4 => {
  if (score >= 80) return 3; // Optimal
  if (score >= 60) return 2; // Normal
  if (score >= 40) return 1; // Borderline
  return 4; // Out of range
};

type TileProps = {
  label: string;
  value: string | number;
  unit?: string;
  score: number;
  selected?: boolean;
};

const MetricTile: React.FC<TileProps> = ({
  label,
  value,
  unit,
  score,
  selected = false,
}) => {
  const theme: Theme = useTheme();
  const cat = scoreToBiomarkerCategory(score);
  const color = getBiomarkerCategoryColor(cat, theme, "biomarkers");
  const tier = BIOMARKER_CATEGORY_LABELS[cat];
  return (
    <Box sx={styles.tile(theme, selected)}>
      <Box sx={styles.label(theme)}>{label}</Box>
      <Box sx={styles.bottomRow}>
        <Box component="span" sx={styles.chip(theme, color)}>
          {value}
          {unit ? ` ${unit}` : ""}
        </Box>
        <Box sx={styles.tierText(theme)}>{tier}</Box>
      </Box>
    </Box>
  );
};

const FatCarbTile: React.FC<{
  fatPct: number;
  carbPct: number;
  selected?: boolean;
}> = ({ fatPct, carbPct, selected = false }) => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.tile(theme, selected)}>
      <Box sx={styles.fatCarbTopRow}>
        <Box sx={styles.fatCarbHalf}>
          <Box component="span" sx={styles.fatCarbLabel(theme)}>
            Fat
          </Box>
          <Box
            component="span"
            sx={styles.chip(theme, theme.colorTokens.P[50])}
          >
            {fatPct}%
          </Box>
        </Box>
        <Box sx={styles.fatCarbHalfEnd}>
          <Box component="span" sx={styles.fatCarbLabel(theme)}>
            Carb
          </Box>
          <Box
            component="span"
            sx={styles.chip(theme, theme.colors_ds.SUCCESS_ICON_MAIN)}
          >
            {carbPct}%
          </Box>
        </Box>
      </Box>
      <Box sx={styles.fatCarbBar(theme)}>
        <Box
          component="span"
          sx={{ ...styles.fatCarbBarFill(theme), width: `${fatPct}%` }}
        />
      </Box>
    </Box>
  );
};

const MainMetricsBar: React.FC = () => {
  const { data, stepper } = useConsultation();
  const activeSubId = stepper.current.sub.id;

  // All 5 tiles ship together in the consultation-prep payload. Backend
  // chooses what fat/carb split to surface here (e.g. base aerobic zone, a
  // weighted average, etc.) — frontend just renders the headline number.
  const { muscle, rmr, vo2, bodyfat, fatcarb } = data.prep!.mainMetricsTiles;
  const { fatPct, carbPct } = fatcarb;

  return (
    <Box sx={styles.bar}>
      <MetricTile
        label="Muscle Mass"
        value={muscle.value}
        unit={muscle.unit ?? "%"}
        score={muscle.score ?? 0}
        selected={tileMatchesActiveSystem("muscle", activeSubId)}
      />
      <MetricTile
        label="Resting Metabolic Rate (RMR)"
        value={rmr.value}
        unit={rmr.unit ?? "kcal/day"}
        score={rmr.score ?? 0}
        selected={tileMatchesActiveSystem("rmr", activeSubId)}
      />
      <MetricTile
        label="VO2max"
        value={vo2.value}
        unit={vo2.unit ?? "ml/kg/min"}
        score={vo2.score ?? 0}
        selected={tileMatchesActiveSystem("vo2", activeSubId)}
      />
      <MetricTile
        label="Body Fat"
        value={bodyfat.value}
        unit={bodyfat.unit ?? "%"}
        score={bodyfat.score ?? 0}
        selected={tileMatchesActiveSystem("bodyfat", activeSubId)}
      />
      <FatCarbTile
        fatPct={fatPct}
        carbPct={carbPct}
        selected={tileMatchesActiveSystem("fatcarb", activeSubId)}
      />
    </Box>
  );
};

export default MainMetricsBar;

const styles = {
  bar: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "16px",
  },
  // Selected state per Figma node 14725:50195: light-blue P[10] background +
  // PRIMARY_TEXT_MAIN border, no drop shadow.
  tile: (theme: Theme, selected: boolean) => ({
    backgroundColor: selected
      ? theme.colorTokens.P[10]
      : theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${
      selected
        ? theme.colors_ds.PRIMARY_TEXT_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    borderRadius: "8px",
    padding: "12px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
    minHeight: "65px",
    overflow: "hidden",
    transition:
      "background-color 120ms ease, border-color 120ms ease",
  }),
  label: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  bottomRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  chip: (theme: Theme, color: string) => ({
    backgroundColor: theme.colorTokens.N[30],
    borderRadius: "4px",
    padding: "4px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "-0.13px",
    color,
  }),
  tierText: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  fatCarbTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  fatCarbHalf: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  fatCarbHalfEnd: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    justifyContent: "flex-end",
  },
  fatCarbLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  fatCarbBar: (theme: Theme) => ({
    position: "relative" as const,
    height: "4px",
    backgroundColor: theme.colors_ds.SUCCESS_ICON_MAIN,
    borderRadius: "38px",
    overflow: "hidden",
  }),
  fatCarbBarFill: (theme: Theme) => ({
    position: "absolute" as const,
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: theme.colorTokens.P[50],
    borderRadius: "38px",
  }),
};
